import {
  processingSessionIdFormat,
  processingSessionItemIdFormat,
} from "@redotech/redo-return-id";
import * as z from "zod";

export const EVIDENCE_CHECK_MAX_PHOTOS = 10;

export const EvidenceCheckResult = z.enum(["match", "mismatch", "unavailable"]);
export type EvidenceCheckResult = z.infer<typeof EvidenceCheckResult>;

/** The checks a mismatch can name: blurry photos, or notes that are one word or nonsense. */
export const EvidenceCheckName = z.enum(["photosClear", "descriptionClear"]);
export type EvidenceCheckName = z.infer<typeof EvidenceCheckName>;

const CheckEvidencePhotosOutput_ = z.object({
  result: EvidenceCheckResult,
  failedChecks: z.array(EvidenceCheckName),
  /** True when there were notes to check. */
  descriptionChecked: z.boolean(),
});
export const CheckEvidencePhotosOutput: z.ZodType<CheckEvidencePhotosOutput> =
  CheckEvidencePhotosOutput_;
export interface CheckEvidencePhotosOutput extends z.infer<
  typeof CheckEvidencePhotosOutput_
> {}

/** The last check of a photo step, logged with the step so finalize can act on a bypass. */
const EvidenceCheckRecord_ = z.object({
  result: EvidenceCheckResult,
  failedChecks: z.array(EvidenceCheckName),
  /** How many times Jev checked this step's evidence, retakes included. */
  attempts: z.number().int().min(1),
  /** True when the processor continued past a mismatch without retaking. */
  bypassed: z.boolean(),
});
export const EvidenceCheckRecord: z.ZodType<EvidenceCheckRecord> =
  EvidenceCheckRecord_;
export interface EvidenceCheckRecord extends z.infer<
  typeof EvidenceCheckRecord_
> {}

/** The unit being photographed: a return's session item, or an item processing session. */
const EvidenceCheckSubject_ = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("return_item"),
    sessionItemId: processingSessionItemIdFormat.schema,
  }),
  z.object({
    type: z.literal("item_session"),
    sessionId: processingSessionIdFormat.schema,
  }),
]);
export type EvidenceCheckSubject = z.infer<typeof EvidenceCheckSubject_>;

export const checkEvidencePhotos = {
  name: "checkEvidencePhotos" as const,
  input: z.object({
    subject: EvidenceCheckSubject_,
    /** The merchant's flow step, for logs; null on capture screens outside the flow. */
    flowStepId: z.string().nullable(),
    photos: z.array(z.string()).min(1).max(EVIDENCE_CHECK_MAX_PHOTOS),
    /** Empty on screens without notes; only the photos are checked then. */
    notes: z.string().trim(),
    /** 1 for the first check of the step, then one more per resubmission. */
    attempt: z.number().int().min(1),
    /** True when the photos differ from the ones checked last on this step. */
    retake: z.boolean(),
  }),
  output: CheckEvidencePhotosOutput_,
};
